import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Calendar, ImageOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const categoryLabels = {
  electronics: 'Electronics',
  vehicles: 'Vehicles',
  fashion: 'Fashion',
  housing: 'Housing',
  furniture: 'Furniture',
  beauty: 'Beauty',
  food: 'Food',
  events: 'Events',
  services: 'Services',
  items: 'Items'
};

export function ListingCard({ listing }) {
  const { id, title, category, description, price, images, image, city, created_at, status } = listing;
  const isSold = status === 'sold';
  const [imgError, setImgError] = useState(false);

  const firstImage = (images && images.length > 0) ? images[0] : image;
  const imageCount = (images && images.length > 0) ? images.length : (image ? 1 : 0);

  const resolveImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith('data:')) return img;
    if (img.startsWith('/api/')) return `${API_URL}${img}`;
    if (img.startsWith('http')) return img;
    return img;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <Link to={`/listing/${id}`} data-testid={`listing-card-${id}`}>
      <Card className="listing-card h-full">
        {/* Image */}
        <div className="aspect-square bg-gray-50 relative overflow-hidden">
          {firstImage && !imgError ? (
            <img 
              src={resolveImageUrl(firstImage)} 
              alt={title}
              onError={() => setImgError(true)}
              className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${isSold ? 'opacity-75' : ''}`}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
              <ImageOff className="h-10 w-10 text-gray-300 mb-1" />
              {imgError && <p className="text-[10px] text-gray-400">Photo unavailable</p>}
            </div>
          )}
          
          {/* SOLD Overlay */}
          {isSold && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center" data-testid={`sold-badge-${id}`}>
              <span className="bg-white text-red-600 text-base font-extrabold px-5 py-2 rounded-lg shadow-lg border-2 border-red-600 tracking-wider">
                SOLD
              </span>
            </div>
          )}

          {/* Category Badge */}
          <Badge className="absolute top-3 left-3 bg-white/90 text-gray-700 backdrop-blur-sm text-xs font-medium">
            {categoryLabels[category] || category}
          </Badge>

          {/* Price Badge */}
          {price && (
            <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg font-bold text-base text-primary shadow-sm">
              {formatPrice(price)}
            </div>
          )}

          {/* Image Count Badge */}
          {imageCount > 1 && (
            <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm px-2 py-0.5 rounded text-xs font-medium text-white flex items-center gap-1">
              {imageCount}
            </div>
          )}
        </div>

        {/* Content */}
        <CardContent className="p-4">
          <h3 className="font-bold text-[15px] text-gray-900 line-clamp-1 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h3>
          <p className="text-[13px] text-gray-500 line-clamp-2 mb-3 leading-relaxed">
            {description}
          </p>
          
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {city ? city.charAt(0).toUpperCase() + city.slice(1) : 'Canada'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(created_at)}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
